import styled from 'styled-components';
import TransactionCases from './PremiumComponents/transactionCases/TransactionCases';
import StatsFilter from './PremiumComponents/StatsFilter';
import { useEffect, useState } from 'react';
import CourtInfo from './PremiumComponents/premiumStats/CourtInfo';
import CaseSearch from './PremiumComponents/premiumStats/CaseSearch';
import RegionBidRateChart from './PremiumComponents/premiumStats/RegionBidRateChart';
import axios from 'axios';
import Auction from './PremiumComponents/auction/Auction';
import { ReportProvider } from '../../context/DetailContext';
import { useUser } from '../../context/UserContext';
import PremiumOnlyAlert from './components/PremiumOnlyAlert';
import PageTop from '../components/pageTop/PageTop';
import { PageContainer } from '../components/common/PageComponent.style';

const apiHost = process.env.REACT_APP_HOST_API;

const PremiumStats = () => {
  const { user, loading } = useUser();
  const isUserReady = !loading && user !== null;
  const [showPremiumAlert, setShowPremiumAlert] = useState(false);

  const [userLevel, setUserLevel] = useState<'sample' | 'premium'>('sample');
  const [caseResult, setCaseResult] = useState<any>(null);
  const [regionBidRates, setRegionBidRates] = useState<any>(null);
  const [courtInfo, setCourtInfo] = useState<any>(null);

  const getRegionBidRatesAndCourtInfo = async (
    address: string,
    category: string,
  ) => {
    try {
      const response = await axios.get(`${apiHost}stat/bid-price-rate`, {
        params: { address, category },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const {
        bidPriceRate = {},
        land = {},
        avgBidDaysByCourt = {},
      } = response.data?.data ?? {};

      setRegionBidRates({
        bidPriceRate,
        land,
        category,
      });
      setCourtInfo(avgBidDaysByCourt);
    } catch (error) {
      console.error(
        '지역/기간별 평균 낙찰가율, 법원 평균 기일/배당 정보 조회 실패',
        error,
      );
      setRegionBidRates(null);
      setCourtInfo(null);
    }
  };

  // 낙찰 사례 필터
  const [filterA, setFilterA] = useState({
    period: '36',
    range: '3000',
    count: '5',
    special: 'none',
  });

  // 거래 사례 필터
  const [filterT, setFilterT] = useState({
    period: '36',
    range: '3000',
    count: '5',
    special: 'none',
  });

  useEffect(() => {
    if (caseResult?.address && caseResult?.name) {
      getRegionBidRatesAndCourtInfo(caseResult.address, caseResult.name);
    }

    if (caseResult) {
      setFilterA({
        period: '36',
        range: '3000',
        count: '5',
        special: 'none',
      });
      setFilterT({
        period: '36',
        range: '3000',
        count: '5',
        special: 'none',
      });
    }
  }, [caseResult]);

  useEffect(() => {
    if (!isUserReady) return;

    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');

    const grade = token ? 'P' : (user?.plan_grade ?? undefined);

    setShowPremiumAlert(grade !== 'P');
  }, [isUserReady, user]);

  useEffect(() => {
    if (!showPremiumAlert) return;

    // 현재 스크롤 위치 기억
    const scrollY = window.scrollY || window.pageYOffset;

    // 레이아웃 흔들림 방지용 (선택)
    document.documentElement.style.scrollbarGutter = 'stable';

    // body를 fixed로 고정해 배경 스크롤 차단
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflowY: body.style.overflowY,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflowY = 'scroll'; // 스크롤바 폭 유지로 레이아웃 점프 방지

    return () => {
      // 원복
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflowY = prev.overflowY;

      // 원래 위치로 복귀
      window.scrollTo(0, scrollY);
    };
  }, [showPremiumAlert]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');

    if (token || user?.plan_grade === 'P') {
      setUserLevel('premium');
    } else {
      setUserLevel('sample');
    }
  }, [user]);

  return (
    <PageContainer $bg="#F4F5F7">
      <PremiumOnlyAlert
        open={showPremiumAlert}
        onClose={() => setShowPremiumAlert(false)}
      />
      <PageTop text="나만의 통계" hasSearch={false} />
      <Wrap>
        <Section>
          <SectionHeader>
            {/* <SectionTitle>나만의 통계</SectionTitle> */}
            <SectionExtra level={userLevel}>
              {userLevel === 'premium' ? '프리미엄' : '샘플'}
            </SectionExtra>
          </SectionHeader>
          {/* 사건번호 검색 + 결과 표시 */}
          <ReportProvider>
            <CaseSearch
              onResult={setCaseResult}
              isPremium={userLevel === 'premium'}
            />
          </ReportProvider>
          {/* 지역/기간별 평균 낙찰가율 */}
          <RegionBidRateChart
            data={regionBidRates}
            isPremium={userLevel === 'premium'}
          />
          {/* 법원 평균 기일/배당 정보 */}
          <CourtInfo data={courtInfo} isPremium={userLevel === 'premium'} />
        </Section>
        {/* 낙찰 사례 */}
        <Section>
          <SectionSubHeader>
            <SectionTitle>낙찰 사례</SectionTitle>
          </SectionSubHeader>
          <SectionDesc>해당 주소 기준 경매, 공매 낙찰 사례</SectionDesc>
          <StatsFilter
            filter={filterA}
            setFilter={setFilterA}
            isPremium={userLevel === 'premium'}
          />
          <Auction
            caseResult={caseResult}
            filter={filterA}
            isPremium={userLevel === 'premium'}
          />
        </Section>
        {/* 거래 사례 */}
        <Section>
          <SectionSubHeader>
            <SectionTitle>거래 사례</SectionTitle>
          </SectionSubHeader>
          <SectionDesc>해당 주소 기준 부동산 실거래 사례</SectionDesc>
          <StatsFilter
            filter={filterT}
            setFilter={setFilterT}
            showSpecial={false}
            isPremium={userLevel === 'premium'}
          />
          <TransactionCases
            caseResult={caseResult}
            filter={filterT}
            isPremium={userLevel === 'premium'}
          />
        </Section>
      </Wrap>
    </PageContainer>
  );
};

export default PremiumStats;

const Wrap = styled.div`
  width: 100%;
  max-width: 1200px;
  overflow-x: hidden;
  gap: 8px;
`;

const Section = styled.div``;

const SectionHeader = styled.div`
  position: fixed;
  top: 0;
  margin-left: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SectionSubHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
`;

const SectionExtra = styled.div<{ level: 'sample' | 'premium' }>`
  position: absolute;
  border-radius: 4px;
  top: 15px;
  left: 110px;
  font-weight: 700;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -0.02em;
  margin-left: 16px;
  height: 24px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${({ level }) => (level === 'premium' ? '#3659d9' : '#5B616E')};
`;

const SectionDesc = styled.span`
  display: block;
  margin: 10px 0;
  color: #424854;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -2%;
  padding-left: 16px;
  padding-right: 16px;
`;
